import React, { useEffect } from 'react';
import styles from './signup-success-step.module.scss';
import { Heading, Link, MenuText } from '@placer-ui/components';
import { VerifyEmailIllustration } from 'features/authentication/signup/components/verify-email-illustration/verify-email-illustration';
import { sendGoogleAnalyticsEvent } from 'utils/google-analytics/google-analytics';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import { signupBaseSplunkData } from 'features/authentication/utils/constants';
import { useHistory, useLocation } from 'react-router-dom';
import { SIGNUP_ROUTE } from 'features/authentication/signup/constants/signup-links';
import { shouldRenderMobileApp } from 'utils/detect-environment';
import classnames from 'classnames';

type SignupSuccessStepProps = {
    userEmail: string;
};

export const SignupSuccessStep = ({ userEmail }: SignupSuccessStepProps) => {
    const sendEvent = useSplunkCallback(signupBaseSplunkData);
    const history = useHistory();
    const location = useLocation();
    const isMobile = shouldRenderMobileApp();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        sendEvent({
            action: 'signup-confirm-viewed',
        });
        sendGoogleAnalyticsEvent({
            category: 'Account Created',
            action: 'email account',
            label: 'confirm email stage',
        });
    }, [sendEvent]);

    useEffect(() => {
        if (!userEmail) {
            history.replace({
                pathname: SIGNUP_ROUTE,
                search: location.search,
            });
        }
    }, [history, location.search, userEmail]);

    return (
        <div className={styles.root}>
            <div className={styles.illustration}>
                <VerifyEmailIllustration isMobile={isMobile} />
            </div>
            <div className={styles.body}>
                <div className={styles.title}>
                    <Heading variant={isMobile ? 'h4' : 'h3'}>
                        Please confirm your email address <br />
                        {userEmail}
                    </Heading>
                </div>
                <MenuText>
                    <div className={styles.message}>
                        We’ve sent an email confirmation to {userEmail} <br />
                        Please check your inbox or spam folder and click the link inside to verify
                        your account.
                    </div>
                </MenuText>
            </div>
            <div className={styles.footer}>
                <MenuText>
                    <span className={classnames({ [styles.contactSupportText]: isMobile })}>
                        Experiencing Problems? Contact
                    </span>
                </MenuText>
                <Link
                    size={isMobile ? 'medium' : 'large'}
                    href="mailto:support@placer.ai"
                    target="_blank"
                    hideUnderline={false}
                >
                    support@placer.ai
                </Link>
            </div>
        </div>
    );
};
